import { BEIGE_400, BLUE_100, BLUE_600, GRAY_100, GRAY_200, GRAY_300, GRAY_400, GRAY_600, GRAY_700, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { CSSProperties, ChangeEvent, FC, ReactNode } from 'react'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUISwitch } from 'components/common/MUISwitch'
import { MUITooltip } from 'components/common/MUITooltip'
import { SliderBar } from 'components/common/SliderBar'

/**
 * Building blocks of the Lab SDK panels (Enhance / Stage when FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK is on).
 * They follow the Playground's list structure — hairline-separated feature blocks with an icon tile,
 * indented sub-options — expressed in the Photo Studio's own tokens.
 */

const BUTTON_RESET = { font: 'inherit', color: 'inherit', textAlign: 'inherit', padding: 0, border: 0, background: 'none', '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: 2 } } as const

const SHADOW_XS = '0px 1px 4px 0px rgba(19, 24, 29, 0.06)'

/** Panel shell: padded scroll area (scrollbar in the right padding) + pinned footer (GenerateButton). */
export const LabPanelShell: FC<{ children: ReactNode, footer: ReactNode }> = ({ children, footer }) => (
  <Stack
    sx={{
      height: '100%',
      maxHeight: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      padding: '2.4rem 2.4rem 0 2.4rem',
      overflow: 'hidden',
      boxSizing: 'border-box',
    }}
  >

    <Stack
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: '1.6rem',
        // The scrollbar lives in the right padding; `scrollbar-gutter: stable` reserves its 8px whether or
        // not the content overflows, so the cards keep one width when a section expands (no jump).
        paddingRight: '1rem',
        marginRight: '-1.8rem',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: BEIGE_400, borderRadius: '4px' },
      }}
    >
      {children}
    </Stack>

    {footer}

  </Stack>
)

/** 16px section headline (Room style, Surfaces, Speed, Included in auto-enhance…) */
export const LabSectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: '1.6rem', fontWeight: 550, color: GRAY_900, lineHeight: 1.5, letterSpacing: '-0.64px' }}>
    {children}
  </Typography>
)

export interface LabSegmentedOption<T extends string> {
  value: T
  label: string
  /** Shown before the label */
  icon?: ReactNode
  /** Shown after the label (e.g. a "New" badge) */
  adornment?: ReactNode
  /** Second line under the label (e.g. Quality's run time); any caption makes every segment two lines tall */
  caption?: string
}

interface LabSegmentedProps<T extends string> {
  options: LabSegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  disabled?: boolean
  /** Basic | Advanced mode control is the large variant */
  large?: boolean
  /** Accessible name of the radio group */
  ariaLabel?: string
}

/** Segmented control in the AI-label vocabulary: GRAY_300 track, raised white selection. */
export function LabSegmented<T extends string>({ options, value, onChange, disabled = false, large = false, ariaLabel }: LabSegmentedProps<T>) {
  const hasCaptions = options.some((option) => option.caption)
  return (
    <Stack
      flexDirection="row"
      gap="0.2rem"
      role="radiogroup"
      aria-label={ariaLabel}
      sx={{ padding: '0.2rem', borderRadius: large ? '0.8rem' : '0.6rem', backgroundColor: GRAY_300, opacity: disabled ? 0.6 : 1 }}
    >
      {options.map((option) => {
        const isSelected = option.value === value
        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            disabled={disabled}
            role="radio"
            aria-checked={isSelected}
            onClick={() => !disabled && onChange(option.value)}
            sx={{
              ...BUTTON_RESET,
              flex: 1,
              height: hasCaptions ? '4.4rem' : large ? '3.6rem' : '3.2rem',
              display: 'flex',
              flexDirection: hasCaptions ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: hasCaptions ? 0 : '0.8rem',
              borderRadius: large ? '0.6rem' : '0.4rem',
              backgroundColor: isSelected ? WHITE : 'transparent',
              boxShadow: isSelected ? SHADOW_XS : 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'background-color .15s ease',
              '& .MuiSvgIcon-root': { fontSize: '1.6rem', color: isSelected ? GRAY_900 : GRAY_700 },
            }}
          >

            {option.icon}

            <Typography sx={{ fontSize: '1.4rem', lineHeight: 1.3, fontWeight: isSelected ? 550 : 450, color: isSelected ? GRAY_900 : GRAY_700, letterSpacing: '-0.56px' }}>
              {option.label}
            </Typography>

            {option.adornment}

            {option.caption && (
              <Typography sx={{ fontSize: '1.2rem', lineHeight: 1.3, fontWeight: 400, color: isSelected ? GRAY_700 : GRAY_600, letterSpacing: '-0.48px' }}>
                {option.caption}
              </Typography>
            )}

          </Box>
        )
      })}
    </Stack>
  )
}

interface LabFeatureBlockProps {
  icon: ReactNode
  label: string
  /** Toggled on → the children (options) are shown; off → hidden and disabled, their values kept by the parent state */
  on?: boolean
  onToggle?: (on: boolean) => void
  disabled?: boolean
  /** First block has no top hairline */
  first?: boolean
  children?: ReactNode
}

/** Playground-style feature row: icon tile · title · switch, with the options indented under the title. */
export const LabFeatureBlock: FC<LabFeatureBlockProps> = ({ icon, label, on = false, onToggle, disabled = false, first = false, children }) => {
  const isExpanded = on && Boolean(children)
  return (
    <Stack sx={{ borderTop: first ? 'none' : `1px solid ${GRAY_300}` }}>

      <Stack flexDirection="row" alignItems="center" gap="1.2rem" sx={{ padding: isExpanded ? '1rem 0 0.2rem' : '1rem 0' }}>

        <Box
          sx={{
            width: '3.6rem',
            height: '3.6rem',
            borderRadius: '0.8rem',
            backgroundColor: GRAY_300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: GRAY_900,
            '& .MuiSvgIcon-root': { fontSize: '1.8rem' },
          }}
        >
          {icon}
        </Box>

        <Typography sx={{ flex: 1, minWidth: 0, fontSize: '1.4rem', fontWeight: 450, lineHeight: 1.4, letterSpacing: '-0.56px', color: GRAY_900 }}>
          {label}
        </Typography>

        {onToggle && <MUISwitch checked={on} onChange={() => onToggle(!on)} disabled={disabled} size="medium" ariaLabel={label} />}

      </Stack>

      {isExpanded && (
        <Stack gap="1.2rem" sx={{ padding: '0 0 1rem 4.8rem' }}>
          {children}
        </Stack>
      )}

    </Stack>
  )
}

export interface LabTile {
  id: string
  label: string
  /** Thumbnail; a tile without one shows `placeholder` instead (e.g. "Describe": Aa) */
  image?: string
  imageStyle?: CSSProperties
  placeholder?: string
}

interface LabTileGridProps {
  tiles: LabTile[]
  selected: string
  onSelect: (id: string) => void
  disabled?: boolean
  columns?: number
  /** Thumbnail height — the sky-style tile is 9.6rem */
  tileHeight?: string
  labelFontSize?: string
}

/** Thumbnail tiles in the sky-style vocabulary (8px radius, 3px BLUE_400 selection ring, label beneath). */
export const LabTileGrid: FC<LabTileGridProps> = ({ tiles, selected, onSelect, disabled = false, columns = 2, tileHeight = '9.6rem', labelFontSize = '1.2rem' }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: '1.2rem' }}>
    {tiles.map((tile) => {
      const isSelected = tile.id === selected
      return (
        <Stack
          key={tile.id}
          gap="0.6rem"
          component="button"
          type="button"
          disabled={disabled}
          role="radio"
          aria-checked={isSelected}
          onClick={() => !disabled && onSelect(tile.id)}
          sx={{ ...BUTTON_RESET, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
        >

          {/* Selected: blue ring, then a 2px white gap, then the image — the outer box keeps its size */}
          <Box
            sx={{
              height: tileHeight,
              borderRadius: '0.8rem',
              overflow: 'hidden',
              border: `2px solid ${isSelected ? BLUE_600 : 'transparent'}`,
              padding: isSelected ? '2px' : 0,
              boxSizing: 'border-box',
              backgroundColor: isSelected ? WHITE : GRAY_100,
              transition: 'border-color .2s ease, padding .2s ease',
            }}
          >
            {tile.image && (
              <img
                src={tile.image}
                alt={tile.label}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: isSelected ? '0.3rem' : 0, ...tile.imageStyle }}
              />
            )}

            {/* No photo to frame it: a discreet stroke marks the tile until it is selected (then the blue ring does) */}
            {!tile.image && (
              <Box
                aria-hidden
                sx={{
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${isSelected ? 'transparent' : GRAY_300}`,
                  borderRadius: isSelected ? '0.3rem' : '0.6rem',
                  backgroundColor: GRAY_200,
                  color: GRAY_600,
                  fontSize: '1.8rem',
                  fontWeight: 550,
                }}
              >
                {tile.placeholder}
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              fontSize: labelFontSize,
              lineHeight: 1.4,
              textAlign: 'center',
              fontWeight: isSelected ? 450 : 350,
              color: isSelected ? GRAY_900 : GRAY_700,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {tile.label}
          </Typography>

        </Stack>
      )
    })}
  </Box>
)

export interface LabSelectOption<T extends string> {
  value: T
  label: string
  /** Colour chip shown before the label (finishes) */
  swatch?: string
}

/** One type style for label, separator and value of a select row, so they always read at the same size */
const SELECT_ROW_TEXT = { fontSize: '1.4rem', lineHeight: 1.4, letterSpacing: '-0.56px' } as const

/** Small colour chip used by the finish selects — hairline border so pale colours stay visible on white */
const LabSwatch: FC<{ color: string }> = ({ color }) => (
  <Box sx={{ width: '1.6rem', height: '1.6rem', borderRadius: '0.4rem', backgroundColor: color, border: `1px solid ${GRAY_400}`, boxSizing: 'border-box', flexShrink: 0 }} />
)

interface LabSelectRowProps<T extends string> {
  icon: ReactNode
  label: string
  value: T
  options: LabSelectOption<T>[]
  onChange: (value: T) => void
  disabled?: boolean
}

/** Select row showing the current choice inline — "Walls · Keep existing" — opening a dropdown of options. */
export function LabSelectRow<T extends string>({ icon, label, value, options, onChange, disabled = false }: LabSelectRowProps<T>) {
  const current = options.find((option) => option.value === value)
  return (
    <MUIDropdown
      placement="bottom-start"
      // Portalled and layered: the All tools sections clip their content (collapse) and the panel scrolls, so an
      // inline menu was cut off under the section below
      disablePortal={false}
      sx={{ zIndex: 'modal', '& .MuiPaper-root': { maxHeight: '18.8rem', overflow: 'hidden' } }}
      button={(isOpen, action, onClose) => (
        <Stack
          flexDirection="row"
          alignItems="center"
          gap="0.8rem"
          component="button"
          type="button"
          disabled={disabled}
          role="button"
          aria-disabled={disabled}
          aria-expanded={isOpen}
          onClick={disabled ? undefined : isOpen ? onClose : action}
          sx={{
            ...BUTTON_RESET,
            width: '100%',
            padding: '1rem 1.2rem',
            borderRadius: '0.8rem',
            border: `1px solid ${GRAY_400}`,
            backgroundColor: WHITE,
            boxShadow: SHADOW_XS,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: 'border-color .2s ease',
            '&:hover': { borderColor: disabled ? GRAY_400 : BLUE_600 },
          }}
        >

          <Box sx={{ display: 'flex', color: GRAY_800, '& .MuiSvgIcon-root': { fontSize: '1.6rem' } }}>{icon}</Box>

          <Stack flexDirection="row" alignItems="center" gap="0.6rem" sx={{ flex: 1, minWidth: 0 }}>

            <Typography sx={{ ...SELECT_ROW_TEXT, fontWeight: 500, color: GRAY_900 }}>{label}</Typography>

            <Typography sx={{ ...SELECT_ROW_TEXT, color: GRAY_600 }}>·</Typography>

            {current?.swatch && <LabSwatch color={current.swatch} />}

            <Typography sx={{ ...SELECT_ROW_TEXT, fontWeight: 400, color: GRAY_700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {current?.label ?? value}
            </Typography>

          </Stack>

          <KeyboardArrowDownRoundedIcon sx={{ fontSize: '1.8rem', color: GRAY_800 }} />

        </Stack>
      )}
    >
      <Stack
        sx={{
          maxHeight: '18.8rem',
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: BEIGE_400, borderRadius: '4px' },
        }}
      >
        {options.map((option) => (
          <MUIDropdownItem key={option.value} onClick={() => onChange(option.value)}>
            <Stack flexDirection="row" alignItems="center" gap="0.8rem">

              {option.swatch && <LabSwatch color={option.swatch} />}

              <span>{option.label}</span>

            </Stack>
          </MUIDropdownItem>
        ))}
      </Stack>
    </MUIDropdown>
  )
}

interface LabSliderProps {
  label: string
  value: number
  valueLabel: string
  min: number
  max: number
  step: number
  onChange: (value: number) => void
  disabled?: boolean
}

/** Slider in the AI-label opacity slider style (4px rail, BLUE_600 track, 20px white thumb) with a quiet label/value line. */
export const LabSlider: FC<LabSliderProps> = ({ label, value, valueLabel, min, max, step, onChange, disabled = false }) => (
  <Stack gap={0}>

    <Stack flexDirection="row" justifyContent="space-between" alignItems="baseline">

      <Typography sx={{ fontSize: '1.4rem', fontWeight: 350, color: GRAY_700, letterSpacing: '-0.56px', lineHeight: 1.4 }}>{label}</Typography>

      {/* Right-aligned with the rail's end (the rail keeps 10px for the thumb) */}
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 350, color: GRAY_700, letterSpacing: '-0.48px', fontVariantNumeric: 'tabular-nums', paddingRight: '10px' }}>{valueLabel}</Typography>

    </Stack>

    <SliderBar
      value={value}
      min={min}
      max={max}
      step={step}
      marks={false}
      valueLabelDisplay="off"
      disabled={disabled}
      onChange={(_, next) => onChange(next as number)}
      aria-label={label}
      sx={{
        // Rail starts flush under the label; 10px (half the thumb) is kept on the right so the thumb at max
        // ends exactly on the content edge instead of overhanging it
        width: 'calc(100% - 10px)',
        maxWidth: 'calc(100% - 10px)',
        margin: 0,
        padding: '0.8rem 0',
        height: 4,
        // Rail and track share the same extent so a full slider reads fully blue (no grey stub at either end)
        '& .MuiSlider-rail': { height: 4, background: GRAY_400, opacity: 1 },
        '& .MuiSlider-track': { height: 4, background: BLUE_600, border: 'none' },
        '& .MuiSlider-thumb': { width: 20, height: 20, background: WHITE, border: `1px solid ${GRAY_400}`, boxShadow: SHADOW_XS },
      }}
    />

  </Stack>
)

interface LabCheckboxProps {
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /** What the option does, in a tooltip behind an "i" next to the label — outside the label, so it never toggles the box */
  info?: string
}

export const LabCheckbox: FC<LabCheckboxProps> = ({ label, checked, onChange, disabled = false, info }) => (
  <Stack flexDirection="row" alignItems="center" gap="0.6rem">

    <MUICheckbox
      checked={checked}
      disabled={disabled}
      onChange={(_: ChangeEvent<HTMLInputElement>, next: boolean) => onChange(next)}
      label={<Typography sx={{ fontSize: '1.4rem', fontWeight: 350, color: GRAY_900, letterSpacing: '-0.56px', lineHeight: 1.4 }}>{label}</Typography>}
    />

    {info && (
      // MUITooltip caps its bubble at 500px; the narrower box keeps a two-sentence explanation in a short column
      <MUITooltip content={<Box sx={{ maxWidth: '24rem' }}>{info}</Box>} placement="top" arrow>
        <Box component="button" type="button" aria-label={info} sx={{ ...BUTTON_RESET, display: 'flex', color: GRAY_600, cursor: 'help', '&:hover': { color: GRAY_800 } }}>
          <InfoOutlinedIcon sx={{ fontSize: '1.4rem' }} />
        </Box>
      </MUITooltip>
    )}

  </Stack>
)

interface LabSectionProps {
  icon: ReactNode
  title: string
  /** Shown under the title while the section is collapsed */
  summary?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Whether the section runs in the next Generate — omitted (no switch) for Adjust, which applies instantly */
  on?: boolean
  onToggle?: (on: boolean) => void
  switchLabel?: string
  disabled?: boolean
  /** First section has no top hairline */
  first?: boolean
  children: ReactNode
}

/**
 * A tool as a collapsible section of the "Display all features at once" list: chevron · icon · title, and the switch
 * that puts it in the next Generate. The header sticks to the top of the list while its options scroll under it.
 */
export const LabSection: FC<LabSectionProps> = ({ icon, title, summary, open, onOpenChange, on = false, onToggle, switchLabel, disabled = false, first = false, children }) => (
  <Box component="section" aria-label={title} sx={{ borderTop: first ? 'none' : `1px solid ${GRAY_300}` }}>

    <Stack
      flexDirection="row"
      alignItems="center"
      gap="1.2rem"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        // Fixed, not a minimum: the summary line shown while collapsed must not change the header's height
        height: '6.4rem',
        // 1.6rem + the list's reserved 8px scrollbar gutter = 2.4rem: the switch lines up with the Generate CTA
        padding: '0 1.6rem 0 1.4rem',
        backgroundColor: WHITE,
        borderBottom: `1px solid ${open ? GRAY_300 : 'transparent'}`,
      }}
    >

      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        sx={{
          ...BUTTON_RESET,
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          borderRadius: '0.8rem',
          cursor: 'pointer',
          '&:hover .lab-section-chevron': { color: GRAY_900 },
        }}
      >

        <KeyboardArrowDownRoundedIcon
          className="lab-section-chevron"
          sx={{ fontSize: '2rem', color: GRAY_600, flexShrink: 0, transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .2s ease, color .2s ease' }}
        />

        <Box sx={{ display: 'flex', flexShrink: 0, color: GRAY_800, '& .MuiSvgIcon-root': { fontSize: '2.2rem' } }}>{icon}</Box>

        <Stack sx={{ minWidth: 0 }}>

          <Typography sx={{ fontSize: '1.6rem', fontWeight: 550, lineHeight: 1.4, letterSpacing: '-0.64px', color: GRAY_900 }}>{title}</Typography>

          {!open && summary && (
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 350, lineHeight: 1.3, letterSpacing: '-0.24px', color: GRAY_700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {summary}
            </Typography>
          )}

        </Stack>

      </Box>

      {onToggle && <MUISwitch checked={on} onChange={() => onToggle(!on)} disabled={disabled} size="medium" ariaLabel={switchLabel} />}

    </Stack>

    {/* Options start under the section icon (header padding 1.4rem + chevron 2rem + gap 1rem), not under the chevron */}
    {open && (
      <Stack sx={{ padding: '1.6rem 1.6rem 2.4rem 4.4rem' }}>
        {children}
      </Stack>
    )}

  </Box>
)

interface LabModeCardProps {
  icon: ReactNode
  label: string
  selected: boolean
  onSelect: () => void
  disabled?: boolean
  badge?: ReactNode
}

/** Generation-mode card in today's Virtual Staging vocabulary (BLUE_600 / BLUE_100 when selected). */
export const LabModeCard: FC<LabModeCardProps> = ({ icon, label, selected, onSelect, disabled = false, badge }) => (
  <Stack
    flexDirection="row"
    alignItems="center"
    gap="0.8rem"
    component="button"
    type="button"
    disabled={disabled}
    role="radio"
    aria-checked={selected}
    onClick={() => !disabled && onSelect()}
    sx={{
      ...BUTTON_RESET,
      width: '100%',
      padding: '1.2rem',
      borderRadius: '0.8rem',
      border: `1px solid ${selected ? BLUE_600 : GRAY_300}`,
      backgroundColor: selected ? BLUE_100 : WHITE,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all .2s ease-in-out',
      '&:hover': { borderColor: selected || disabled ? undefined : GRAY_400 },
    }}
  >

    <Box sx={{ display: 'flex', color: GRAY_800, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: '1.6rem' } }}>{icon}</Box>

    <Typography sx={{ flex: 1, fontSize: '1.4rem', fontWeight: selected ? 500 : 400, color: selected ? GRAY_900 : GRAY_700, lineHeight: 1.4, letterSpacing: '-0.56px' }}>
      {label}
    </Typography>

    {badge}

  </Stack>
)
